
import { CssBaseline } from '@mui/material';
import { grey } from '@mui/material/colors';
import * as Sentry from '@sentry/react';
import { useContext } from 'react';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider } from 'react-router-dom';

import ParticipantProvider from '@/components/providers/data/ParticipantProvider';
import Participant from '@/pages/Participant';
import Participants from '@/pages/Participants';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RouteErrorBoundary from './components/errors/RouteErrorBoundary';
import DefaultLayout from './components/layout/DefaultLayout';
import ServiceStackClientProvider from './components/providers/apis/ServiceStackClientProvider';
import AuthProvider from './components/providers/auth/AuthProvider';
import LoadingProvider from './components/providers/data/LoadingProvider';
import TrialProvider from './components/providers/data/TrialProvider';
import TrialsProvider from './components/providers/data/TrialsProvider';
import ThemeProvider from './components/providers/styles/ThemeProvider';
import AlertContext from './contexts/data/AlertContext';
import { BreadcrumbProvider } from './contexts/layout/BreadcrumbContext';
import Reports from './pages/Reports';
import Trials from './pages/Trials';
import DefaultThemeOptions from './styles/DefaultThemeOptions';

declare let global: any;

if (typeof global === "undefined") {
    (window as any).global = window;
}

const user = global.AUTH as any;

const routes: RouteObject[] = [
    {
        path: '/',
        element: <DefaultLayout />,
        ErrorBoundary: RouteErrorBoundary,
        children: [
            { index: true, element: <Trials /> },
            {
                path: ':trialCode',
                element: <TrialProvider />,
                children: [
                    {
                        index: true,
                        element: (
                            <ProtectedRoute>
                                <Participants />
                            </ProtectedRoute>
                        )
                    },
                    {
                        path: ':participantCode',
                        element: <ParticipantProvider />,
                        children: [
                            {
                                index: true,
                                element: (
                                    <ProtectedRoute>
                                        <Participant />
                                    </ProtectedRoute>
                                )
                            }
                        ]
                    }
                ]
            },
            { path: 'reports', element: <Reports /> },
            { path: '*', element: <Navigate to="/" /> }
        ]
    }
];

const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)(routes);

function App() {
    const { addAlert } = useContext(AlertContext);

    return (
        <ServiceStackClientProvider
            basePath="/api"
            baseUrl={import.meta.env.VITE_API_URL}
            onAlert={addAlert}
        >
            <ThemeProvider 
                themeOptions={DefaultThemeOptions}
                beforeThemeCreated={(themeOptions) => {
                    const newOptions = {
                        ...themeOptions,
                        palette: {
                            primary: {
                                main: '#56287B'
                            },
                            secondary: {
                                main: '#EC008C',
                                dark: '#b31573'
                            },
                            info: {
                                main: '#76CED9'
                            },
                            success: {
                                light: '#81e3de',
                                // main: '#27DFD1',
                                main: '#17a2b8',
                                dark: '#1baba3'
                            },
                            warning: {
                                light: '#ffcdb3',
                                main: '#ffa06e',
                                dark: '#f25400'
                            },
                            error: {
                                light: '#f7a6cb',
                                main: '#ed539a',
                                dark: '#DB0065'
                            },
                            background: {
                                default: grey[200]
                            }
                        },
                    };
        
                    return newOptions;
                }}
            >
                <CssBaseline />
                <LoadingProvider>
                    <AuthProvider initialValue={user}>
                        <BreadcrumbProvider>
                            <TrialsProvider>
                                <RouterProvider router={router} />
                            </TrialsProvider>
                        </BreadcrumbProvider>
                    </AuthProvider>
                </LoadingProvider>
            </ThemeProvider>
        </ServiceStackClientProvider>
    )
}

export default App
